/* eslint-disable indent */
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import styled from 'styled-components';

import { COLORS, MEDIA } from '../../../theme';

export const StyledCard = styled(Card)`
  && {
    position: relative;
    border-color: ${COLORS.black06};
    border: 1px solid transparent;
    border-radius: ${({ theme }) => theme.borderRadius.primary};

    &.MuiPaper-elevation1 {
      box-shadow: 0px 3px 10px 0px rgba(0, 0, 0, 0.03);
    }

    &.MuiCard-colorSecondary {
      background-color: ${COLORS.black02};
      border-color: ${COLORS.black08};
    }

    &.MuiCard-sizeLarge > .MuiCardHeader-root,
    &.MuiCard-sizeLarge > .MuiCardContent-root {
      padding: ${({ theme }) => theme.spacing.m4};
    }

    &.MuiCard-sizeLarge > .MuiCardHeader-root {
      padding-bottom: ${({ theme }) => theme.spacing.l4};
    }

    &.MuiCard-sizeMedium > .MuiCardHeader-root,
    &.MuiCard-sizeMedium > .MuiCardContent-root {
      padding: ${({ theme }) => theme.spacing.m2};
    }

    &.MuiCard-sizeMedium > .MuiCardHeader-root {
      padding-bottom: ${({ theme }) => theme.spacing.m4};
    }

    &.MuiCard-sizeSmall > .MuiCardHeader-root,
    &.MuiCard-sizeSmall > .MuiCardContent-root {
      padding: ${({ theme }) => theme.spacing.m1};
    }

    &.MuiCard-sizeSmall > .MuiCollapse-root {
      margin-top: -${({ theme }) => theme.spacing.s2};
    }

    &.MuiCard-sizeThin > .MuiCardContent-root {
      padding: ${({ theme }) => theme.spacing.s3};
      ${({ theme }) => theme.spacing.m1};
    }

    &.MuiCard-sizeTiny > .MuiCardContent-root {
      padding: ${({ theme }) => `2px ${theme.spacing.s4}`};
    }

    ${({ $clickable }) =>
      $clickable &&
      `
        && {
          cursor: pointer;
        }
      `}
  }

  + .MuiPaper-root {
    margin-top: ${({ theme }) => theme.spacing.s3};
  }

  ${({ variant }) =>
    variant === 'outlined' &&
    `
      &&& {
        border-color: ${COLORS.black06};
      }
  `}

  ${({ $hasError }) =>
    $hasError &&
    `
      &&& {
        border-color: ${COLORS.red};
      }
  `}

  ${({ $selected }) =>
    $selected &&
    `
      &&& {
        border-color: ${COLORS.kinetikBlue};
      }
  `}
`;

export const StyledCardContent = styled(CardContent)`
  && {
    padding: ${({ theme }) => theme.spacing.m1};
  }
  ${({ size, theme }) =>
    size === 'tiny' &&
    `
    &&&& {
      padding: 14px ${theme.spacing.s4};
    }
  `};
  ${({ paddingTop }) =>
    paddingTop &&
    `
    &&&& {
      padding-top: ${paddingTop};
    }
  `};
  padding-bottom: 0 !important;
`;

export const StyledCardHeader = styled(CardHeader)`
  & + ${StyledCardContent}, & + .MuiCollapse-root ${StyledCardContent} {
    padding-top: 0 !important;
  }

  .MuiCardHeader-content .MuiTypography-root {
    font-size: ${({ $variant, theme }) => theme.fontSize[$variant ?? 'h4']};
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    line-height: ${({ $variant, theme }) => theme.lineHeight[$variant ?? 'h4']};
  }

  .MuiCardHeader-subheader.MuiTypography-root {
    font-size: ${({ theme }) => theme.fontSize.h6};
    font-weight: ${({ theme }) => theme.fontWeight.medium};
    line-height: ${({ theme }) => theme.lineHeight.h6};
    color: ${COLORS.black40};
    padding-top: ${({ theme }) => theme.spacing.s1};
  }

  .MuiCardHeader-action {
    margin: 0;
  }

  ${({ $short, theme }) =>
    $short &&
    `
      &&&& {
        padding-bottom: ${theme.spacing.s2};
      }
  `}

  ${({ $size, theme }) =>
    $size === 'small' &&
    `
      &&&& {
        padding-top: ${theme.spacing.s4};
        padding-bottom: ${theme.spacing.s4};
      }
  `}

  ${({ $size, theme }) =>
    $size === 'tiny' &&
    `
      &&&& {
        padding: 14px ${theme.spacing.s4};
      }
  `}


  ${({ $subtitle, theme }) =>
    $subtitle &&
    `
      &&&& {
        padding-bottom: ${theme.spacing.s2};
      }
  `}

  ${({ $breakpoint }) => MEDIA[$breakpoint ?? 'mobileDown']} {
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing.s4};

    .MuiCardHeader-avatar {
      margin: 0;
    }

    .MuiCardHeader-action {
      width: 100%;
      margin-bottom: ${({ theme }) => theme.spacing.s4};
    }
  }
`;

export const CardSubheader = styled(Box)`
  font-size: ${({ theme }) => theme.fontSize.body2};
  line-height: ${({ theme }) => theme.lineHeight.body2};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  color: ${COLORS.black60};
  margin: ${({ theme }) => theme.spacing.s3} ${({ theme }) => theme.spacing.m1};
`;
